import React, { useState, useMemo, useCallback } from 'react'

import { date } from 'utils'

import { useCanAccess, useDebouncedValue } from 'hooks'
import { useSubscriptionsQuery } from 'hooks/api'
import type { SubscriptionRow } from 'hooks/api/useSubscriptionsQuery'
import { CreditCardIcon, CircleXIcon } from 'lucide-react'

import { useTheme } from '@mui/material'

import {
  Header,
  Table,
  EmptyState,
  ActionDropdown,
  CompanyCell,
  StatusChip,
  CancelSubscriptionInternalModal,
} from 'components'
import { INTERNAL_SUBSCRIPTION_STATUSES } from 'constants/statuses'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'

// VERA-1096: Deferred (no backend yet): assign plan, change plan, reactivate.
// TODO(VERA-1096-followup): add assign/change/reactivate once backend Stripe
// resume is available. Tracked in context/tech-debt.md.

// Filter options for the Interval column — surfaced as a multiSelect dropdown
// inside the unified Filter side panel (not a standalone header dropdown). The
// Status column uses colored status pills (statusFilterOptions) instead.
const INTERVAL_FILTER_OPTIONS = ['month', 'year']

const formatCurrency = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const SubscriptionsPage = () => {
  const theme = useTheme()

  // Pagination + sort state
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | undefined>(undefined)

  // Search state (raw + debounced)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedQ] = useDebouncedValue(searchInput, 300)

  const filters = {
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    sortField,
    sortDir,
    q: debouncedQ || undefined,
  }

  const [selectedRow, setSelectedRow] = useState<SubscriptionRow | null>(null)
  const [openCancelModal, setOpenCancelModal] = useState(false)

  const { data, refetch, isLoading } = useSubscriptionsQuery(filters)
  const rows = data?.data || []
  const total = data?.total || 0

  const { isHasAccess: isCanSuspendCompany } = useCanAccess({ role: 'platform_admin' })

  const handleCancelSubscription = useCallback((row: SubscriptionRow) => {
    setSelectedRow(row)
    setOpenCancelModal(true)
  }, [])

  const handleCloseCancelModal = useCallback(() => {
    setOpenCancelModal(false)
  }, [])

  // §27 — the row's only kebab item stays visible+disabled+tooltip without the role (was
  // hidden entirely, so a non-admin opened the kebab onto nothing). `visible:` keeps the
  // record-state check (a subscription id must exist to cancel); the role check moves to
  // `disabled` + `tooltip`.
  const getActionItems = useCallback(
    (row: SubscriptionRow) => [
      {
        label: 'Cancel subscription',
        onClick: () => handleCancelSubscription(row),
        visible: !!row.stripe_subscription_id,
        disabled: !isCanSuspendCompany,
        tooltip: isCanSuspendCompany ? undefined : 'Only a Vera admin can cancel a subscription.',
        destructive: true,
        icon: <CircleXIcon strokeWidth={1} color={theme.palette.error.main} />,
      },
    ],
    [isCanSuspendCompany, theme.palette.error.main, handleCancelSubscription],
  )

  const columns = useMemo(
    () => [
      {
        field: 'company_name',
        headerName: 'Company',
        type: 'string',
        disableReorder: true,
        pinned: 'left',
        width: 260,
        renderCell: ({ row }: { row: SubscriptionRow }) => (
          <CompanyCell
            company={{
              id: row.company_id,
              name: row.company_name,
              logo: row.company_logo ?? '',
            }}
          />
        ),
      },
      {
        field: 'plan_title',
        headerName: 'Plan',
        type: 'string',
        width: 160,
        renderCell: ({ row }: { row: SubscriptionRow }) => row.plan_title || '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'statusChip',
        options: statusFilterOptions(INTERNAL_SUBSCRIPTION_STATUSES),
        width: 160,
        renderCell: ({ row }: { row: SubscriptionRow }) => (
          <StatusChip
            statuses={INTERNAL_SUBSCRIPTION_STATUSES}
            status={row.status}
            size="small"
          />
        ),
      },
      {
        field: 'mrr',
        headerName: 'MRR',
        type: 'number',
        width: 120,
        renderCell: ({ row }: { row: SubscriptionRow }) =>
          row.mrr > 0 ? formatCurrency(row.mrr) : '—',
      },
      {
        field: 'plan_interval',
        headerName: 'Interval',
        type: 'multiSelect',
        options: INTERVAL_FILTER_OPTIONS,
        width: 100,
        renderCell: ({ row }: { row: SubscriptionRow }) =>
          row.plan_interval === 'year'
            ? 'Yearly'
            : row.plan_interval === 'month'
            ? 'Monthly'
            : '—',
      },
      {
        field: 'current_period_end',
        headerName: 'Period end',
        width: 160,
        type: 'dateTime',
        valueGetter: (_: any, row: SubscriptionRow) =>
          row.current_period_end ? new Date(row.current_period_end) : null,
        renderCell: ({ value }: { value: Date | null }) =>
          value ? date(value) : '—',
      },
      {
        field: 'actions',
        headerName: false,
        disableReorder: true,
        // Standing rule (2026-08-12): permission never lives in `visible` — the
        // kebab itself always renders so the disabled+tooltip on the item inside
        // (see getActionItems above) is reachable. This used to gate the whole
        // cell on isCanSuspendCompany, which made that tooltip unreachable for
        // non-admins (empty cell instead of a disabled item explaining why).
        renderCell: ({ row }: { row: SubscriptionRow }) => (
          <ActionDropdown items={getActionItems(row)} />
        ),
        align: 'center',
        width: 56,
        pinned: 'right',
      },
    ],
    [getActionItems],
  )

  // Adapt SubscriptionRow to Company shape for CancelSubscriptionInternalModal
  const selectedCompany = selectedRow
    ? {
        id: selectedRow.company_id,
        name: selectedRow.company_name,
        logo: selectedRow.company_logo ?? '',
        subscription: {
          id: selectedRow.id,
          status: selectedRow.status,
          stripe_subscription_id: selectedRow.stripe_subscription_id,
        },
      }
    : null

  return (
    <>
      <Header title="Subscriptions" total={total} />
      <Table
        fillBottom
        loading={isLoading}
        onSearchChange={(value) => {
          setSearchInput(value)
          setPaginationModel(prev => ({ ...prev, page: 0 }))
        }}
        rows={rows}
        columns={columns}
        getRowId={(row: SubscriptionRow) => row.id}
        paginationMode="server"
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        sortingMode="server"
        onSortModelChange={(model) => {
          const first = model[0]
          if (first) {
            setSortField(first.field)
            setSortDir(first.sort as 'asc' | 'desc')
          } else {
            setSortField(undefined)
            setSortDir(undefined)
          }
          setPaginationModel(prev => ({ ...prev, page: 0 }))
        }}
        emptyState={
          <EmptyState
            title="No subscriptions found"
            text="Subscriptions appear here when companies are assigned a plan"
            icon={<CreditCardIcon strokeWidth={1} size={32} />}
          />
        }
      />
      <CancelSubscriptionInternalModal
        company={selectedCompany as any}
        open={openCancelModal}
        onClose={handleCloseCancelModal}
        refetch={refetch}
      />
    </>
  )
}
